/* eslint-disable react-refresh/only-export-components --
 * Context module: Provider + hook are intentionally co-located.
 */
import React, { createContext, useContext, useMemo } from 'react';
import type { ClinicPhysicianResponse } from '../../services/api/types.gen';

export interface PhysicianRoster {
  /** Active physicians with an NPI, sorted by name. */
  physicians: ClinicPhysicianResponse[];
  isLoading: boolean;
  /** Set when the roster could not be loaded; the field falls back to free text. */
  error: string | null;
  refetch: () => void;
  /** Whether to offer the "add a physician" affordance at all. */
  canCreate: boolean;
  /**
   * Clinic a new physician would be created in, or `null` to leave the
   * affordance inert. Null keeps the control visible without a clinic to
   * write to — which is what a layout preview wants, and what a real form
   * never has.
   */
  createClinicId: string | null;
}

const PhysicianRosterContext = createContext<PhysicianRoster | null>(null);

/**
 * A roster supplied by an ancestor instead of fetched.
 *
 * `null` — the usual case — means the field loads the clinic's own roster.
 * A provider is how a surface that has no clinic (the Forms Manager layout
 * preview) hands over a stand-in without the field knowing why.
 */
export function useSuppliedPhysicianRoster(): PhysicianRoster | null {
  return useContext(PhysicianRosterContext);
}

export function PhysicianRosterProvider({
  roster,
  children,
}: {
  roster: PhysicianRoster;
  children: React.ReactNode;
}) {
  const value = useMemo(() => roster, [roster]);
  return (
    <PhysicianRosterContext.Provider value={value}>
      {children}
    </PhysicianRosterContext.Provider>
  );
}
